// Service layer for the AyushPay Labs — Diagnostic Management module.
// Wired to the diagnostic/chains backend (internalapi v4, ops + superadmin).

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getApiRole = (uiRole) => {
  const roleMapping = {
    SuperAdmin: "superadmin",
    Finance: "finance",
    Ops: "ops",
    Support: "support",
  };
  return roleMapping[uiRole] || "ops";
};

const makeRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found. Please login first.");
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);

  const isMultipart = options.body instanceof FormData;

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method: options.method || "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        // Let the browser set the multipart boundary for FormData uploads.
        ...(isMultipart ? {} : { "Content-Type": "application/json" }),
        ...options.headers,
      },
      body: options.body,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText || "Unknown API error";

      try {
        const parsed = JSON.parse(errorText);
        if (Array.isArray(parsed?.msg)) {
          // contract: loan-and-subscription/core/response_engine/error_msg.py:invalid_json (msg=[{key, value}])
          errorMessage =
            parsed.msg.map((m) => m.value).filter(Boolean).join(" ") || errorText;
        } else {
          errorMessage = parsed?.msg?.error || parsed?.msg?.status || errorText;
        }
      } catch {
        // keep raw text
      }

      throw new Error(errorMessage);
    }

    return response.json();
  } catch (error) {
    if (error.name === "AbortError") {
      throw new Error("Request timeout. Please try again.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// GET v4/{role}/diagnostic/chains/list/  — search + status/city filter + pagination
export const getDiagnosticChains = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const queryParams = new URLSearchParams();

  queryParams.append("page", String(params.page || 1));
  queryParams.append("items", String(params.items || 20));

  if (params.search?.trim()) queryParams.append("search", params.search.trim());
  if (params.city?.trim()) queryParams.append("city", params.city.trim());
  if (params.status?.trim()) queryParams.append("status", params.status.trim());

  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/chains/list/?${queryParams.toString()}`
  );
};

// POST v4/{role}/diagnostic/chains/create/  — quick-create (brand_name + primary_contact)
export const createDiagnosticChain = async (role, payload) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/chains/create/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

// GET v4/{role}/diagnostic/chains/{id}/detail/
export const getDiagnosticChainDetail = async (role, chainId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/chains/${chainId}/detail/`);
};

// PUT v4/{role}/diagnostic/chains/{id}/edit/  — partial update of scalar/JSON fields
export const editDiagnosticChain = async (role, chainId, payload) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/chains/${chainId}/edit/`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

// PUT v4/{role}/diagnostic/chains/{id}/edit/  — logo upload via multipart (partial)
// POST v4/{role}/diagnostic/chains/{id}/logo/  — logo stored as a generic Document
export const uploadChainLogo = async (role, chainId, file) => {
  const apiRole = getApiRole(role);
  const form = new FormData();
  form.append("file", file);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/chains/${chainId}/logo/`, {
    method: "POST",
    body: form,
  });
};

// GET v4/{role}/diagnostic/chains/stats/  — single call feeding the 4 KPI cards
export const getDiagnosticChainStats = async (role) => {
  const apiRole = getApiRole(role);
  const resp = await makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/chains/stats/`);
  return resp?.msg || resp;
};

export const CHAIN_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

// --------------------------------------------------------------------------- //
// Labs (PRD section 6 / 7.1)
// --------------------------------------------------------------------------- //

// GET v4/{role}/diagnostic/labs/list/
export const getDiagnosticLabs = async (role, params = {}) => {
  const apiRole = getApiRole(role);
  const q = new URLSearchParams();
  q.append("page", String(params.page || 1));
  q.append("items", String(params.items || 20));
  ["search", "chain", "exclude_chain", "status", "city", "state", "pincode", "type", "lab_type", "updated_by"].forEach((k) => {
    if (params[k]?.toString().trim()) q.append(k, params[k].toString().trim());
  });
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/list/?${q.toString()}`);
};

// GET v4/{role}/diagnostic/labs/stats/  — feeds the 6 KPI cards
export const getDiagnosticLabStats = async (role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/stats/`);
};

// GET v4/{role}/diagnostic/labs/updated-by-options/  — [{value, label, count}] for the "Updated By" filter
export const getDiagnosticLabUpdatedByOptions = async (role) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/updated-by-options/`);
};

// POST v4/{role}/diagnostic/labs/create/
export const createDiagnosticLab = async (role, payload) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/create/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

// GET v4/{role}/diagnostic/labs/{id}/detail/
export const getDiagnosticLabDetail = async (role, labId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/detail/`);
};

// POST v4/{role}/diagnostic/geocode/  — derive lat/long from address fields (our geocoder)
export const geocodeLabAddress = async (role, payload) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/geocode/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

// PUT v4/{role}/diagnostic/labs/{id}/edit/
export const editDiagnosticLab = async (role, labId, payload) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/edit/`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
};

// POST v4/{role}/diagnostic/labs/{id}/photos/  — one photo as a generic Document
export const uploadLabPhoto = async (role, labId, photoType, file) => {
  const apiRole = getApiRole(role);
  const form = new FormData();
  form.append("photo_type", photoType);
  form.append("file", file);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/photos/`, {
    method: "POST",
    body: form,
  });
};

// POST v4/{role}/diagnostic/labs/{id}/photos/{document_id}/delete/  — soft-delete one photo
export const deleteLabPhoto = async (role, labId, documentId) => {
  const apiRole = getApiRole(role);
  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/photos/${documentId}/delete/`,
    { method: "POST" }
  );
};

// POST v4/{role}/diagnostic/labs/{id}/documents/  — one compliance file as a Document
export const uploadLabDocument = async (role, labId, docType, file) => {
  const apiRole = getApiRole(role);
  const form = new FormData();
  form.append("doc_type", docType);
  form.append("file", file);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/documents/`, {
    method: "POST",
    body: form,
  });
};

// POST v4/{role}/diagnostic/labs/{id}/documents/{document_id}/delete/  — soft-delete one document
export const deleteLabDocument = async (role, labId, documentId) => {
  const apiRole = getApiRole(role);
  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/documents/${documentId}/delete/`,
    { method: "POST" }
  );
};

// POST v4/{role}/diagnostic/labs/{id}/tests/  — upload the lab's test-menu sheet (new version)
export const uploadLabTestSheet = async (role, labId, file) => {
  const apiRole = getApiRole(role);
  const form = new FormData();
  form.append("file", file);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/tests/`, {
    method: "POST",
    body: form,
  });
};

// GET v4/{role}/diagnostic/lab-tests/template/  — downloads the AyushPay xlsx template (binary)
export const downloadLabTestTemplate = async (role) => {
  const apiRole = getApiRole(role);
  const token = localStorage.getItem("authToken");
  const resp = await fetch(
    `${BASE_URL}/internal/apis/v4/${apiRole}/diagnostic/lab-tests/template/`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!resp.ok) throw new Error("Failed to download template.");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "ayushpay_lab_tests_template.xlsx";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const LAB_TEST_SHEET_ACCEPT = ".xlsx,.xls,.csv";

// Shared fetch wrapper for the bulk-upload endpoints below — own timeout,
// not `makeRequest` (its 12s timeout is too short for a file upload on a
// slow connection). Reuses this file's existing error-parsing convention.
const fetchAuthed = async (endpoint, { method = "GET", body, timeoutMs, expect = "json" } = {}) => {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("No authentication token found. Please login first.");

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers: { Authorization: `Bearer ${token}` },
      body,
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = errorText || "Unknown API error";
      try {
        const parsed = JSON.parse(errorText);
        if (Array.isArray(parsed?.msg)) {
          errorMessage = parsed.msg.map((m) => m.value).filter(Boolean).join(" ") || errorText; // ok: filters empty error-message strings, not a contract field like active/status
        } else {
          errorMessage = parsed?.msg?.error || parsed?.msg?.status || errorText;
        }
      } catch {
        // keep raw text
      }
      throw new Error(errorMessage);
    }

    return expect === "blob" ? response.blob() : response.json();
  } catch (error) {
    if (error.name === "AbortError") throw new Error("Request timeout. Please try again.");
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// GET v4/{role}/diagnostic/labs/bulk-upload/template/ — downloads the bulk-onboarding xlsx template
export const downloadLabBulkTemplate = async (role) => {
  const apiRole = getApiRole(role);
  return fetchAuthed(`/internal/apis/v4/${apiRole}/diagnostic/labs/bulk-upload/template/?_=${Date.now()}`, {
    timeoutMs: 15000,
    expect: "blob",
  });
};

// POST v4/{role}/diagnostic/labs/bulk-upload/ — upload a filled sheet to bulk-create labs.
// Response is `{ queued: true, document_id }`. There is still no live progress/job model —
// document_id is only used for a single (or manually-retried) status check via
// getLabBulkUploadStatus, not for real-time polling.
export const bulkUploadLabs = async (role, file) => {
  const apiRole = getApiRole(role);
  const formData = new FormData();
  formData.append("file", file);
  return fetchAuthed(`/internal/apis/v4/${apiRole}/diagnostic/labs/bulk-upload/`, {
    method: "POST",
    body: formData,
    timeoutMs: 30000,
    expect: "json",
  });
};

// GET v4/{role}/diagnostic/labs/bulk-upload/{document_id}/status/ — one-shot (or manually
// re-clicked) status check, not a polling loop. While processing: { processing: true }.
// Once done: { processing: false, created_count, skipped_count, dropped_count, error_file_url }.
export const getLabBulkUploadStatus = async (role, documentId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/bulk-upload/${documentId}/status/`);
};

export const LAB_BULK_UPLOAD_ACCEPT = ".xlsx,.xls,.csv";

// --------------------------------------------------------------------------- //
// National-lab serviceable pincodes — replace-all with versioning.
// A National lab (vendor.service_model === "national") is serviced by an
// uploaded set of pincodes instead of a single address + radius. Each upload
// supersedes the prior set; prior versions are retained for audit + rollback.
// These mirror the labs/bulk-upload/ convention above (fetchAuthed for uploads,
// one-shot status check — no live polling job model yet).
// --------------------------------------------------------------------------- //

export const LAB_PINCODE_UPLOAD_ACCEPT = ".xlsx,.xls,.csv";

// GET v4/{role}/diagnostic/labs/pincodes/template/ — blank serviceable-pincode xlsx template
export const downloadLabPincodeTemplate = async (role) => {
  const apiRole = getApiRole(role);
  return fetchAuthed(`/internal/apis/v4/${apiRole}/diagnostic/labs/pincodes/template/?_=${Date.now()}`, {
    timeoutMs: 15000,
    expect: "blob",
  });
};

// POST v4/{role}/diagnostic/labs/{id}/pincodes/upload/ — upload a pincode sheet.
// Replace-all: the parsed set becomes a NEW active version. Response is
// `{ queued: true, document_id }`; poll once via getLabPincodeUploadStatus.
export const uploadLabPincodes = async (role, labId, file) => {
  const apiRole = getApiRole(role);
  const formData = new FormData();
  formData.append("file", file);
  return fetchAuthed(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/pincodes/upload/`, {
    method: "POST",
    body: formData,
    timeoutMs: 30000,
    expect: "json",
  });
};

// GET v4/{role}/diagnostic/labs/{id}/pincodes/upload/{document_id}/status/ — one-shot check.
// While processing: { processing: true }. Once done:
// { processing: false, added, invalid_count, version, error_file_url }.
export const getLabPincodeUploadStatus = async (role, labId, documentId) => {
  const apiRole = getApiRole(role);
  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/pincodes/upload/${documentId}/status/`
  );
};

// GET v4/{role}/diagnostic/labs/{id}/pincodes/versions/ — version history for audit + rollback.
// Returns { active_version, versions: [{ version, count, uploaded_at, uploaded_by, source_file }] }.
export const getLabPincodeVersions = async (role, labId) => {
  const apiRole = getApiRole(role);
  return makeRequest(`/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/pincodes/versions/`);
};

// POST v4/{role}/diagnostic/labs/{id}/pincodes/versions/{version}/rollback/ — one-click rollback:
// makes a prior version the active set (append-only audit trail preserved server-side).
export const rollbackLabPincodeVersion = async (role, labId, version) => {
  const apiRole = getApiRole(role);
  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/pincodes/versions/${version}/rollback/`,
    { method: "POST" }
  );
};

// GET v4/{role}/diagnostic/labs/{id}/tests/list/  — parsed tests (search + type + pagination)
export const getLabTests = async (role, labId, params = {}) => {
  const apiRole = getApiRole(role);
  const q = new URLSearchParams();
  q.append("page", String(params.page || 1));
  q.append("items", String(params.items || 50));
  if (params.search?.trim()) q.append("search", params.search.trim());
  if (params.type?.trim()) q.append("type", params.type.trim());
  return makeRequest(
    `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/tests/list/?${q.toString()}`
  );
};

// --------------------------------------------------------------------------- //
// Chain test lists — v4/{role}/diagnostic/chains/{chainId}/test-lists/...
// A list owns its tests; member labs are assigned to a list (one list per lab).
// Errors keep the backend's `code` + details (e.g. reassign_required conflicts)
// so the UI can react precisely instead of showing a generic message.
// --------------------------------------------------------------------------- //

export const CHAIN_TEST_SHEET_ACCEPT = ".xlsx,.csv";
export const CHAIN_TEST_SHEET_MAX_BYTES = 10 * 1024 * 1024;

const chainTestListsPath = (role, chainId) =>
  `/internal/apis/v4/${getApiRole(role)}/diagnostic/chains/${chainId}/test-lists/`;

// contract: loan-and-subscription/core/response_engine/drf_ext.py (msg envelope) —
// msg is a string, {status, code, ...} or [{key, value}] (serializer errors).
const toApiError = (status, text) => {
  let body = null;
  try { body = JSON.parse(text); } catch { /* non-JSON body */ }
  const msg = body?.msg;
  let message = "Request failed. Please try again.";
  if (typeof msg === "string") message = msg;
  else if (Array.isArray(msg)) message = msg.map((m) => m.value).filter(Boolean).join(" ") || message; // ok: filters empty error strings, not a contract field
  else if (msg?.status || msg?.error) message = msg.status || msg.error;
  const error = new Error(message);
  error.status = status;
  error.code = msg && typeof msg === "object" && !Array.isArray(msg) ? msg.code || null : null;
  error.details = msg && typeof msg === "object" ? msg : null;
  return error;
};

const chainRequest = async (endpoint, { method = "GET", json, timeoutMs = 20000, signal } = {}) => {
  const token = localStorage.getItem("authToken");
  if (!token) throw new Error("No authentication token found. Please login first.");
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) signal.addEventListener("abort", () => controller.abort(), { once: true });
  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: json !== undefined ? JSON.stringify(json) : undefined,
      signal: controller.signal,
    });
    const text = await response.text();
    if (!response.ok) throw toApiError(response.status, text);
    return JSON.parse(text)?.msg;
  } catch (error) {
    if (error.name === "AbortError") {
      if (signal?.aborted) throw error;
      throw new Error("Request timeout. Please try again.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Multipart upload via XHR so the UI can show real byte progress (fetch can't).
const uploadWithProgress = (endpoint, formData, onProgress) =>
  new Promise((resolve, reject) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      reject(new Error("No authentication token found. Please login first."));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${BASE_URL}${endpoint}`);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.timeout = 120000;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)?.msg); } catch { reject(new Error("Unexpected server response.")); }
      } else {
        reject(toApiError(xhr.status, xhr.responseText));
      }
    };
    xhr.onerror = () => reject(new Error("Network error — check your connection and try again."));
    xhr.ontimeout = () => reject(new Error("Upload timed out. Please try again."));
    xhr.send(formData);
  });

// GET  …/test-lists/ → { chain, lists[] }
export const getChainTestLists = (role, chainId, opts) =>
  chainRequest(chainTestListsPath(role, chainId), opts);

// GET  …/test-lists/member-labs/ → { items[], total }
export const getChainMemberLabs = (role, chainId, opts) =>
  chainRequest(`${chainTestListsPath(role, chainId)}member-labs/`, opts);

// GET  …/test-lists/{listId}/ → list + latest_upload + uploads[] (polled while processing)
export const getChainTestList = (role, chainId, listId, opts) =>
  chainRequest(`${chainTestListsPath(role, chainId)}${listId}/`, opts);

// POST …/test-lists/ (multipart name + file) → { test_list }
export const createChainTestList = (role, chainId, { name, file }, onProgress) => {
  const form = new FormData();
  form.append("name", name);
  form.append("file", file);
  return uploadWithProgress(chainTestListsPath(role, chainId), form, onProgress);
};

// POST …/test-lists/{listId}/uploads/ (multipart file) → list payload
export const uploadChainTestListVersion = (role, chainId, listId, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  return uploadWithProgress(`${chainTestListsPath(role, chainId)}${listId}/uploads/`, form, onProgress);
};

// PATCH …/test-lists/{listId}/ — { name } rename · { active: false } archive
export const updateChainTestList = (role, chainId, listId, payload) =>
  chainRequest(`${chainTestListsPath(role, chainId)}${listId}/`, { method: "PATCH", json: payload });

// PUT  …/test-lists/{listId}/assignments/ — full set of labs using the list.
// Throws error.code === "reassign_required" (error.details.conflicts) unless allowReassign.
export const setChainTestListAssignments = (role, chainId, listId, labIds, allowReassign = false) =>
  chainRequest(`${chainTestListsPath(role, chainId)}${listId}/assignments/`, {
    method: "PUT",
    json: { lab_ids: labIds, allow_reassign: allowReassign },
  });

// PATCH …/test-lists/{listId}/tests/{testId}/ — edit one chain-list test in place
// (applies to every lab using the list). Send only changed fields + `updated_at`.
export const updateChainTestListTest = (role, chainId, listId, testId, payload) =>
  chainRequest(`${chainTestListsPath(role, chainId)}${listId}/tests/${testId}/`, { method: "PATCH", json: payload });

// PATCH v4/{role}/diagnostic/labs/{labId}/tests/{testId}/ — edit one test of a lab's own
// (direct) menu. Refused with code "chain_managed" while the lab uses a chain list.
export const updateLabTest = (role, labId, testId, payload) =>
  chainRequest(`/internal/apis/v4/${getApiRole(role)}/diagnostic/labs/${labId}/tests/${testId}/`, {
    method: "PATCH",
    json: payload,
  });

// GET  …/test-lists/{listId}/tests/ — same item shape as getLabTests
export const getChainTestListTests = (role, chainId, listId, params = {}) => {
  const q = new URLSearchParams();
  q.append("page", String(params.page || 1));
  q.append("items", String(params.items || 20));
  if (params.search?.trim()) q.append("search", params.search.trim());
  if (params.type?.trim()) q.append("type", params.type.trim());
  return chainRequest(`${chainTestListsPath(role, chainId)}${listId}/tests/?${q.toString()}`);
};

// --------------------------------------------------------------------------- //
// Lab operations & schedule — wraps the backend vendor_operation API.
// GET/POST/PUT v4/{role}/diagnostic/labs/{id}/operations/
// --------------------------------------------------------------------------- //

// GET — returns the lab's operations config, or null when not configured yet.
// "operations not configured" is the expected empty state, not an error.
export const getLabOperations = async (role, labId) => {
  const apiRole = getApiRole(role);
  try {
    const resp = await makeRequest(
      `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/operations/`
    );
    return resp?.msg || resp;
  } catch (err) {
    if (/not configured/i.test(err.message || "")) return null;
    throw err;
  }
};

// POST (create) / PUT (update) the operations config. `existed` picks the verb;
// if the backend disagrees ("operations already/not configured"), we retry once
// with the other verb — so a stale `existed` flag (e.g. another Ops user created
// the row meanwhile) self-corrects instead of surfacing a confusing error.
export const saveLabOperations = async (role, labId, payload, existed) => {
  const apiRole = getApiRole(role);
  const url = `/internal/apis/v4/${apiRole}/diagnostic/labs/${labId}/operations/`;
  const send = (method) => makeRequest(url, { method, body: JSON.stringify(payload) });
  try {
    const resp = await send(existed ? "PUT" : "POST");
    return resp?.msg || resp;
  } catch (err) {
    const msg = err.message || "";
    // "already configured" never contains "not configured", so these are exclusive.
    if (!existed && /already configured/i.test(msg)) {
      const resp = await send("PUT");
      return resp?.msg || resp;
    }
    if (existed && /not configured/i.test(msg)) {
      const resp = await send("POST");
      return resp?.msg || resp;
    }
    throw err;
  }
};

// Operating-hours weekdays — keys MUST match backend vendor/models/vendor_operation.py WEEKDAYS.
export const WEEKDAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
];

export const WEEKDAY_LABELS = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};

// A sensible starting weekly schedule: weekdays + Saturday open 09:00–18:00,
// Sunday off. Ops adjusts from here; lunch is left blank (optional).
export const DEFAULT_OPERATING_SLOT = WEEKDAYS.reduce((acc, day) => {
  acc[day] = {
    active: day !== "sunday",
    open_time: "09:00",
    close_time: "18:00",
    lunch_start: "",
    lunch_end: "",
  };
  return acc;
}, {});

// Vocabularies — must mirror diagnostic/models/constants.py exactly.
export const LAB_TYPE_OPTIONS = [
  { value: "pathology", label: "Pathology" },
  { value: "radiology", label: "Radiology" },
  { value: "multi_modality", label: "Multi-Modality" },
  { value: "collection_center", label: "Collection Center" },
];

// Lab types that require >= 1 modality (conditional, PRD 7.1).
export const LAB_TYPES_REQUIRING_MODALITIES = ["radiology", "multi_modality"];

export const LAB_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
];

export const VERIFICATION_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "rejected", label: "Rejected" },
];

// Vendor delivery mode — online = API-integrated (Healthians), offline = onboarded lab.
export const VENDOR_TYPE_OPTIONS = [
  { value: "online", label: "Online" },
  { value: "offline", label: "Offline" },
];

export const SPECIALIZATION_OPTIONS = [
  { value: "womens_wellness", label: "Women's Wellness" },
  { value: "pediatric", label: "Pediatric" },
  { value: "cardiac", label: "Cardiac" },
  { value: "diabetes", label: "Diabetes" },
  { value: "allergy", label: "Allergy" },
  { value: "oncology", label: "Oncology" },
  { value: "fertility", label: "Fertility" },
  { value: "sports_medicine", label: "Sports Medicine" },
];

export const MODALITY_OPTIONS = [
  { value: "x_ray", label: "X-Ray" },
  { value: "usg", label: "USG" },
  { value: "ecg", label: "ECG" },
  { value: "ct", label: "CT" },
  { value: "mri", label: "MRI" },
  { value: "mammography", label: "Mammography" },
  { value: "opg", label: "OPG" },
  { value: "tmt", label: "TMT" },
  { value: "echo", label: "ECHO" },
  { value: "pft", label: "PFT" },
  { value: "dexa", label: "DEXA" },
];

export const CONTACT_TYPE_OPTIONS = [
  { value: "receptionist", label: "Receptionist" },
  { value: "spoc", label: "SPOC" },
  { value: "owner", label: "Owner" },
  { value: "phlebo", label: "Phlebo Coordinator" },
  { value: "manager", label: "Manager / GM" },
  { value: "billing", label: "Billing" },
  { value: "escalation", label: "Escalation" },
];

// Contact types that surface a PAN field (owner verification, PRD).
export const CONTACT_TYPES_WITH_PAN = ["owner"];
// Contact types that surface a WhatsApp field (home-collection coordination, PRD).
export const CONTACT_TYPES_WITH_WHATSAPP = ["phlebo"];

// India's scheduled languages + English. Searchable; Ops can also add others.
export const LANGUAGE_OPTIONS = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "bengali", label: "Bengali" },
  { value: "marathi", label: "Marathi" },
  { value: "telugu", label: "Telugu" },
  { value: "tamil", label: "Tamil" },
  { value: "gujarati", label: "Gujarati" },
  { value: "urdu", label: "Urdu" },
  { value: "kannada", label: "Kannada" },
  { value: "odia", label: "Odia" },
  { value: "malayalam", label: "Malayalam" },
  { value: "punjabi", label: "Punjabi" },
  { value: "assamese", label: "Assamese" },
  { value: "maithili", label: "Maithili" },
  { value: "santali", label: "Santali" },
  { value: "kashmiri", label: "Kashmiri" },
  { value: "nepali", label: "Nepali" },
  { value: "konkani", label: "Konkani" },
  { value: "sindhi", label: "Sindhi" },
  { value: "dogri", label: "Dogri" },
  { value: "manipuri", label: "Manipuri (Meitei)" },
  { value: "bodo", label: "Bodo" },
  { value: "sanskrit", label: "Sanskrit" },
];

// Photos & Gallery (PRD 7.2). Keys mirror the backend single-slot photo types.
export const LAB_PHOTO_SLOTS = [
  { key: "logo", label: "Logo", hint: "≤2MB · square, transparent preferred" },
  { key: "exterior", label: "Exterior / Signboard", hint: "Local-location trust" },
  { key: "reception", label: "Reception / Waiting Area", hint: "Hygiene signal" },
  { key: "sample_collection", label: "Sample Collection Room", hint: "Hygiene signal" },
  { key: "equipment", label: "Equipment / Lab Area", hint: "Capability signal" },
  { key: "pathologist", label: "Chief Pathologist", hint: "Headshot" },
];
export const LAB_GALLERY_MAX = 10;

// Compliance & Documents (PRD — Certifications). `multi` types hold several files.
export const LAB_DOC_TYPES = [
  { key: "registration", label: "Registration Certificate", required: true, multi: false, hint: "Clinical Establishment cert" },
  { key: "owner_kyc", label: "Owner KYC Documents", required: true, multi: true, hint: "Aadhaar / PAN / passport" },
  { key: "agreement", label: "Signed Agreement", required: true, multi: false, hint: "Signed vendor agreement PDF" },
  { key: "cancelled_cheque", label: "Cancelled Cheque", required: true, multi: false, hint: "For bank verification" },
  { key: "biomedical", label: "Biomedical Waste Authorization", required: false, multi: false },
  { key: "license", label: "State Pathology / Radiology License", required: false, multi: false },
  { key: "dpa", label: "Data Processing Agreement", required: false, multi: false, hint: "Offline-signed OK in Phase 1" },
];
export const LAB_DOC_KYC_MAX = 10;
export const LAB_DOC_ACCEPT = ".pdf,.jpg,.jpeg,.png";

export const labelFor = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";

// Who created / last updated a lab, for display. The backend answers "Unknown"
// when no history row exists — labs onboarded before tracking started — and
// "System" for writes with no signed-in user behind them (bulk upload, Celery).
// "Unknown" is a real answer but it reads like a person's name in a table, so
// it renders as N/A, the same as an empty Updated By in the Hospital list.
export const AUDIT_UNKNOWN = "Unknown";

export const auditName = (entry) => {
  const name = entry?.name;
  return !name || name === AUDIT_UNKNOWN ? "N/A" : name;
};

// --------------------------------------------------------------------------- //
// Frontend field validation (guardrails before the API is hit)
// Each validator returns "" when valid, or a human-readable message when not.
// Sanitizers strip disallowed characters as the user types.
// --------------------------------------------------------------------------- //

export const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MOBILE_REGEX = /^[6-9]\d{9}$/; // Indian mobile: 10 digits, starts 6-9

// Earliest plausible establishment year for a diagnostic business.
const MIN_ESTABLISHED_YEAR = 1850;

// Keep only letters, spaces and a few place-name punctuation marks ( . ' - ).
export const sanitizePlace = (val = "") => val.replace(/[^a-zA-Z\s.'-]/g, "");

export const validatePlace = (val = "", label = "This field") => {
  const v = val.trim();
  if (!v) return "";
  if (!/^[a-zA-Z][a-zA-Z\s.'-]*$/.test(v)) return `${label} should contain letters only.`;
  return "";
};

export const validateEstablishedYear = (val = "") => {
  const v = String(val).trim();
  if (!v) return ""; // optional field
  if (!/^\d{4}$/.test(v)) return "Enter a 4-digit year.";
  const year = Number(v);
  const current = new Date().getFullYear();
  if (year < MIN_ESTABLISHED_YEAR || year > current) {
    return `Year must be between ${MIN_ESTABLISHED_YEAR} and ${current}.`;
  }
  return "";
};

export const validateGstin = (val = "") => {
  const v = val.trim();
  if (!v) return ""; // optional
  if (v.length !== 15) return "GSTIN must be 15 characters.";
  if (!GSTIN_REGEX.test(v)) return "Enter a valid GSTIN (e.g. 22AAAAA0000A1Z5).";
  return "";
};

export const validateIfsc = (val = "") => {
  const v = val.trim();
  if (!v) return "";
  if (!IFSC_REGEX.test(v)) return "Enter a valid IFSC (e.g. HDFC0001234).";
  return "";
};

export const validatePan = (val = "") => {
  const v = val.trim();
  if (!v) return "";
  if (!PAN_REGEX.test(v)) return "Enter a valid PAN (e.g. ABCDE1234F).";
  return "";
};

export const validateEmail = (val = "") => {
  const v = val.trim();
  if (!v) return "";
  if (!EMAIL_REGEX.test(v)) return "Enter a valid email address.";
  return "";
};

export const validateMobile = (val = "", label = "Mobile") => {
  const v = val.trim();
  if (!v) return "";
  if (!MOBILE_REGEX.test(v)) return `${label} must be a 10-digit number starting 6-9.`;
  return "";
};

export const validatePincode = (val = "") => {
  const v = val.trim();
  if (!v) return "";
  if (!/^\d{6}$/.test(v)) return "Pincode must be 6 digits.";
  return "";
};

// Renders an ISO timestamp as "06 Jun 2026, 06:03 PM" (date + time, local zone).
export const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
};
